import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banner, Heading, Spinner } from "@panelgenie/shared-ui";
import {
  EMPTY_FILTERS,
  InvocationFilters,
  type FilterState,
} from "../../components/InvocationFilters.js";
import { InvocationTable } from "../../components/InvocationTable.js";

/**
 * List view — filtered, paged summaries from `support.listInvocations`.
 *
 * Two filter states: `draft` is the live bar; `applied` is what the
 * query reads. "Apply" commits the draft and resets paging, so typing a
 * user id doesn't refire the query on every keystroke. A click on a row
 * navigates to the detail view by invocation id.
 *
 * Rows whose outcome is "processing" are in-flight scan jobs the server
 * projects into the list; while any are visible the query polls so the
 * row flips to its recorded outcome without a manual refresh. The
 * Resubmit action (panel-scan rows) resets the panel's breaker data to
 * the pre-scan placeholder and re-runs the same photos. The previous
 * run's derived images are preserved (KAN-85) so staff can compare
 * imagery + output across runs — destructive enough to warrant the
 * confirm dialog, cheap enough that the dialog is all it warrants.
 */
export const Route = createFileRoute("/")({
  component: InvocationsListPage,
});

const PAGE_SIZE = 50;

function InvocationsListPage() {
  const { trpc, clerkDashboardUserUrlTemplate } = Route.useRouteContext();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["invocations", "list", applied, offset],
    queryFn: () =>
      trpc.support.listInvocations.query({
        ...(applied.userId.trim() !== ""
          ? { userId: applied.userId.trim() }
          : {}),
        ...(applied.kind !== "" ? { kind: applied.kind } : {}),
        ...(applied.outcome !== "" ? { outcome: applied.outcome } : {}),
        limit: PAGE_SIZE,
        offset,
      }),
    // While any row is still processing, poll so the row flips to its
    // recorded outcome (and the fresh invocation appears) on its own.
    refetchInterval: (q) =>
      q.state.data?.items.some((item) => item.outcome === "processing") === true
        ? 5_000
        : false,
  });

  const resubmit = useMutation({
    mutationFn: (id: string) =>
      trpc.support.resubmitInvocation.mutate({ id }),
    // Refetch on success *and* failure — a CONFLICT ("already running")
    // means there's a processing row this page hasn't seen yet.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["invocations", "list"],
      });
    },
  });

  const onResubmit = (id: string) => {
    const confirmed = window.confirm(
      "Resubmit this scan?\n\n" +
        "This clears the panel's breaker data back to the pre-scan " +
        "placeholder and re-runs the same submitted photos. The previous " +
        "run's derived images are kept for cross-run comparison.",
    );
    if (confirmed) resubmit.mutate(id);
  };

  const deleteInvocation = useMutation({
    mutationFn: (id: string) => trpc.support.deleteInvocation.mutate({ id }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["invocations", "list"],
      });
    },
  });

  const onDelete = (id: string) => {
    const confirmed = window.confirm(
      "Delete this invocation?\n\n" +
        "This permanently removes the invocation record and its " +
        "system-generated derived images. The user's submitted photos " +
        "stay on their panels — only photos no longer attached to any " +
        "panel are reclaimed. This cannot be undone.",
    );
    if (confirmed) deleteInvocation.mutate(id);
  };

  const apply = () => {
    setApplied(draft);
    setOffset(0);
  };
  const clear = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setOffset(0);
  };

  const goToDetail = (id: string) => {
    void navigate({ to: "/invocations/$id", params: { id } });
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-6xl flex-col gap-6 overflow-y-auto px-4 py-6">
      <div>
        <Heading level="h1">AI invocations</Heading>
        <p className="mt-1 text-base text-mute">
          Every recorded scan, most-recent-first. Open one to see the
          submitted photos and the raw model response.
        </p>
      </div>

      <InvocationFilters
        value={draft}
        onChange={setDraft}
        onApply={apply}
        onClear={clear}
        busy={query.isFetching}
      />

      {resubmit.isError && (
        <Banner variant="danger" title="Resubmit failed">
          {resubmit.error instanceof Error
            ? resubmit.error.message
            : "Request failed."}
        </Banner>
      )}

      {deleteInvocation.isError && (
        <Banner variant="danger" title="Delete failed">
          {deleteInvocation.error instanceof Error
            ? deleteInvocation.error.message
            : "Request failed."}
        </Banner>
      )}

      {query.isError ? (
        <Banner variant="danger" title="Couldn't load invocations">
          {query.error instanceof Error
            ? query.error.message
            : "Request failed. Staff access is required for this surface."}
        </Banner>
      ) : query.isPending ? (
        <div className="flex items-center gap-3 text-mute">
          <Spinner size="md" />
          <span>Loading invocations…</span>
        </div>
      ) : (
        <>
          <InvocationTable
            items={query.data.items}
            onSelect={goToDetail}
            onResubmit={onResubmit}
            resubmittingId={resubmit.isPending ? resubmit.variables : null}
            onDelete={onDelete}
            deletingId={deleteInvocation.isPending ? deleteInvocation.variables : null}
            clerkDashboardUserUrlTemplate={clerkDashboardUserUrlTemplate}
          />
          <Pagination
            offset={offset}
            pageSize={PAGE_SIZE}
            count={query.data.items.length}
            hasMore={query.data.hasMore}
            busy={query.isFetching}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

function Pagination({
  offset,
  pageSize,
  count,
  hasMore,
  busy,
  onPrev,
  onNext,
}: {
  readonly offset: number;
  readonly pageSize: number;
  readonly count: number;
  readonly hasMore: boolean;
  readonly busy: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
}) {
  if (offset === 0 && !hasMore) return null;
  const first = count === 0 ? 0 : offset + 1;
  const last = offset + count;
  return (
    <div
      aria-label="pagination"
      className="flex items-center justify-between text-sm text-mute"
    >
      <span>
        {first}–{last}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          className="rounded-md border border-line px-3 py-1.5 disabled:opacity-40"
          onClick={onPrev}
          disabled={offset === 0 || busy}
        >
          Previous
        </button>
        <button
          type="button"
          className="rounded-md border border-line px-3 py-1.5 disabled:opacity-40"
          onClick={onNext}
          disabled={!hasMore || busy}
        >
          Next
        </button>
      </div>
    </div>
  );
}
